import { Link, useInRouterContext, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from "react";
import { ModeToggle } from "./theme-toggle";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/lib/store/store";
import { loadFromStorage, logout } from "@/lib/store/auth/authSlice";
import { useQueryClient } from "@tanstack/react-query";
import { Search, ShoppingCart, Menu as MenuIcon, X, User, ChevronDown, Store } from "lucide-react";
import { useGetCart } from "@/lib/hooks/public/use-cart";
import { useCategories, useSubCategories } from "@/lib/hooks/public/use-catalog";
import type { Category } from "@/lib/types/catalog";
import { useBlogEnabled } from "@/lib/hooks/useBlogEnabled";
import { trackSearch } from "@/src/services/analytics";

export function Navbar() {
  const queryClient = useQueryClient();
  // useNavigate is the direct React Router 7 hook. The previous implementation
  // used useSafeRouter, which returned a new object on every render and was
  // a footgun for useEffect deps.
  //
  // `useInRouterContext()` returns false if the component is rendered outside
  // a <Router> (test isolation, future SSR/Storybook use). In that case we
  // fall back to a hard navigation so logout/search still work rather than
  // crashing. In the normal mounted-by-DefaultLayout path, this is always
  // true and the navigate() path is used.
  const inRouter = useInRouterContext();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: cart } = useGetCart();
  const { userType } = useSelector((state: RootState) => state.auth);
  const loggedIn = userType === "User"
  const cartItemCount = cart?.items?.reduce((total, item) => total + item.quantity, 0) ?? 0;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [craftHubOpen, setCraftHubOpen] = useState(false);
  const [mobileCraftHubOpen, setMobileCraftHubOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [mobileSupplierOpen, setMobileSupplierOpen] = useState(false);
  const supplierTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const menuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const profileTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const craftHubTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: categories } = useCategories({ loadImages: false });
  const blogEnabled = useBlogEnabled();
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [subCategoryCache, setSubCategoryCache] = useState<Record<number, Category[]>>({});

  const { data: fetchedSubCategories } = useSubCategories(activeCategoryId ?? 0, {
    enabled: !!activeCategoryId && !subCategoryCache[activeCategoryId ?? 0],
  });

  const activeCategorySubs = activeCategoryId !== null
    ? (subCategoryCache[activeCategoryId] ?? fetchedSubCategories ?? [])
    : [];

  useEffect(() => {
    if (activeCategoryId && fetchedSubCategories) {
      setSubCategoryCache(prev => ({ ...prev, [activeCategoryId]: fetchedSubCategories }));
    }
  }, [activeCategoryId, fetchedSubCategories]);

  useEffect(() => {
    dispatch(loadFromStorage());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
      if (profileTimeoutRef.current) clearTimeout(profileTimeoutRef.current);
      if (craftHubTimeoutRef.current) clearTimeout(craftHubTimeoutRef.current);
      if (supplierTimeoutRef.current) clearTimeout(supplierTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [mobileMenuOpen]);

  // Sync search query with URL parameter.
  // Depend on the stable string primitive rather than the router object
  // (which gets a new reference every render and would reset the input on each keystroke).
  const [navSearchParams] = useSearchParams();
  const keywordsParam = navSearchParams.get('keywords') ?? "";
  useEffect(() => {
    setSearchQuery(keywordsParam);
  }, [keywordsParam]);

  const handleMouseEnter = (menu: string) => {
    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    setActiveMegaMenu(menu);
    if (menu === "categories" && (categories?.length ?? 0) > 0) {
      setActiveCategory(0);
      setActiveCategoryId(categories?.[0]?.id ?? null);
    }
  };

  const handleMouseLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMegaMenu(null);
      setActiveCategory(null);
    }, 150);
  };

  const handleProfileEnter = () => {
    if (profileTimeoutRef.current) clearTimeout(profileTimeoutRef.current);
    setProfileMenuOpen(true);
  };

  const handleProfileLeave = () => {
    profileTimeoutRef.current = setTimeout(() => {
      setProfileMenuOpen(false);
    }, 150);
  };

  const handleCraftHubEnter = () => {
    if (craftHubTimeoutRef.current) clearTimeout(craftHubTimeoutRef.current);
    setCraftHubOpen(true);
  };

  const handleCraftHubLeave = () => {
    craftHubTimeoutRef.current = setTimeout(() => {
      setCraftHubOpen(false);
    }, 150);
  };

  const handleSupplierEnter = () => {
    if (supplierTimeoutRef.current) clearTimeout(supplierTimeoutRef.current);
    setSupplierOpen(true);
  };

  const handleSupplierLeave = () => {
    supplierTimeoutRef.current = setTimeout(() => {
      setSupplierOpen(false);
    }, 150);
  };

  const handleLogout = () => {
    dispatch(logout());
    queryClient.clear();
    if (inRouter) {
      navigate("/login");
    } else if (typeof window !== 'undefined') {
      window.location.href = "/login";
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      trackSearch(trimmedQuery);
      const target = `/search?keywords=${encodeURIComponent(trimmedQuery)}`;
      if (inRouter) {
        navigate(target);
      } else if (typeof window !== 'undefined') {
        window.location.href = target;
      }
      setSearchQuery("");
      setMobileSearchOpen(false);
    }
  };

  return (
    <>
      <nav className="sticky top-0 z-50 w-full border-b bg-background/90 shadow-sm" aria-label="Main">
        <div className="mx-auto max-w-11/12">
          <div className="flex h-16 items-center gap-4 lg:h-[72px]">
            {/* Logo */}
            <Link to={loggedIn ? "/catalog/" : "/"} className="flex items-center" onClick={() => setMobileMenuOpen(false)}>
              <img src="/bevfed_logo.png" alt="Beverage Federation" width={160} height={48} className="h-[58px] w-auto lg:h-[66px]" />
            </Link>
            <div className="lg:hidden">
              <button className="flex items-center justify-center min-h-11 min-w-11 p-2" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
                <MenuIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Mobile: Hamburger and Login */}
            <div className="flex items-center gap-2 lg:hidden ml-auto">
              {!loggedIn && (
                <Link to="/login" className="h-11 px-4 py-2 has-[>svg]:px-3 rounded-md border border-2 border-secondary dark:border-primary shadow-xs font-medium flex items-center justify-center transition-all hover:bg-secondary/10 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/80">
                  Login
                </Link>
              )}
              {loggedIn && (
                <div className="flex items-center gap-2 justify-end">
                  <button
                    onClick={() => setMobileSearchOpen(true)}
                    className="flex items-center justify-center min-h-11 min-w-11 p-2"
                    aria-label="Open search"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                  <Link to="/cart" className="relative">
                    <Button variant="ghost" size="icon" className="relative">
                      <ShoppingCart className="h-5 w-5" />
                      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
                        {cartItemCount}
                      </span>
                    </Button>
                  </Link>
                  <div className="relative" onMouseEnter={handleProfileEnter} onMouseLeave={handleProfileLeave}>
                    <Button variant="ghost" size="icon" className="rounded-full" aria-label="Profile">
                      <User className="h-10 w-10 text-secondary dark:text-white" />
                    </Button>
                    {profileMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-48 rounded-md border bg-popover p-2 shadow-lg z-50">
                        <Link to="/myaccount" className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted">My Account</Link>
                        <Link to="/login" className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted" onClick={() => { setMobileMenuOpen(false); handleLogout(); }}>Log out</Link>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden flex-1 items-center gap-2 lg:flex lg:ml-8">
              <div className="flex items-center gap-1">

                {/* Marketplace - only when logged in */}
                {loggedIn && (
                  <div className="relative" onMouseEnter={() => handleMouseEnter("categories")} onMouseLeave={handleMouseLeave}>
                    <Button variant="ghost" className="nav-link gap-1 font-medium" aria-expanded={activeMegaMenu === "categories"}>
                      <Store className="h-4 w-4" />
                      Marketplace
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {!loggedIn && (
                  <>
                    <Link to="/about" className="nav-link font-medium inline-flex items-center h-9 px-4 py-2 rounded-md hover:text-accent-foreground transition-colors">About</Link>
                    <Link to="/request-demo" className="nav-link font-medium inline-flex items-center h-9 px-4 py-2 rounded-md hover:text-accent-foreground transition-colors">Demo</Link>
                    
                  </>
                )}
              </div>
              <Link to="/food" className="nav-link font-medium inline-flex items-center h-9 px-4 py-2 rounded-md hover:text-accent-foreground transition-colors whitespace-nowrap">Food Program</Link>
              

              {/* Craft Business Hub dropdown */}
              <div
                className="relative"
                onMouseEnter={handleCraftHubEnter}
                onMouseLeave={handleCraftHubLeave}
              >
                <Link
                  to="/craft-business-hub"
                  className="nav-link gap-1 font-medium inline-flex items-center h-9 px-4 py-2 rounded-md hover:text-accent-foreground transition-colors"
                  aria-haspopup="true"
                  aria-expanded={craftHubOpen}
                >
                  Craft Business Hub
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${craftHubOpen ? "rotate-180" : ""}`} />
                </Link>
                {craftHubOpen && (
                  <div
                    role="menu"
                    className="absolute left-0 top-full mt-1 w-64 rounded-md border bg-popover p-2 shadow-lg z-50"
                  >
                    {blogEnabled && (
                      <Link
                        to="/blog"
                        role="menuitem"
                        className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                      >
                        Blog
                      </Link>
                    )}
                    <Link
                      to="/champions-program"
                      role="menuitem"
                      className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                      onClick={() => setCraftHubOpen(false)}
                    >
                      Champion Program
                    </Link>
                    <Link
                      to="/contract-co-packing-directory"
                      role="menuitem"
                      className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                      onClick={() => setCraftHubOpen(false)}
                    >
                      Contracting &amp; Co-Packing Directory
                    </Link>
                    <Link
                      to="/events"
                      role="menuitem"
                      className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                      onClick={() => setCraftHubOpen(false)}
                    >
                      Events
                    </Link>
                    <a
                      href="https://www.beveragefederation.com/catalog/strategy-and-support"
                      role="menuitem"
                      className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                      onClick={() => setCraftHubOpen(false)}
                    >
                      Services &amp; Strategy
                    </a>
                  </div>
                )}
              </div>

              {/* Supplier dropdown */}
              <div
                className="relative"
                onMouseEnter={handleSupplierEnter}
                onMouseLeave={handleSupplierLeave}
              >
                <button
                  className="nav-link gap-1 font-medium inline-flex items-center h-9 px-4 py-2 rounded-md hover:text-accent-foreground transition-colors"
                  aria-haspopup="true"
                  aria-expanded={supplierOpen}
                >
                  Suppliers & Partners
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${supplierOpen ? "rotate-180" : ""}`} />
                </button>
                {supplierOpen && (
                  <div
                    role="menu"
                    className="absolute left-0 top-full mt-1 w-56 rounded-md border bg-popover p-2 shadow-lg z-50"
                  >
                    <a
                      href="https://www.beveragefederation.com/association-program"
                      role="menuitem"
                      className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                      onClick={() => setSupplierOpen(false)}
                    >
                      Association Program
                    </a>
                    <a
                      href="https://www.beveragefederation.com/become-a-supplier"
                      role="menuitem"
                      className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                      onClick={() => setSupplierOpen(false)}
                    >
                      Become a Supplier
                    </a>
                    <a
                      href="https://www.beveragefederation.com/portal/login"
                      role="menuitem"
                      className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                      onClick={() => setSupplierOpen(false)}
                    >
                      Partner Portal
                    </a>
                  </div>
                )}
              </div>

              {/* Search Bar (Desktop, only when logged in) */}
              {loggedIn && (
                <form onSubmit={handleSearch} className="flex-1 max-w-md mx-4">
                  <div className="relative">
                    <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                      type="search"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pr-10"
                    />
                  </div>
                </form>
              )}

              {/* Right Controls */}
              <div className="flex items-center gap-2 ml-auto">
                {/* Cart (only visible for logged in people) */}
                {loggedIn && (
                  <Link to="/cart" className="relative">
                    <Button variant="ghost" size="icon" className="relative">
                      <ShoppingCart className="h-5 w-5" />
                      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
                        {cartItemCount}
                      </span>
                    </Button>
                  </Link>
                )}

                {/* Auth buttons */}
                {!loggedIn && (
                  <>
                    <div className="flex items-center space-x-2">
                      <Link
                        to="/login"
                        className="h-9 px-4 py-2 has-[>svg]:px-3 rounded-md border border-2 border-secondary dark:border-primary shadow-xs font-medium flex items-center justify-center transition-all hover:bg-secondary/10 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/80"
                      >
                        Login
                      </Link>
                      <Link
                        to="/join"
                        className="h-9 px-4 py-2 has-[>svg]:px-3 rounded-md bg-secondary text-white font-medium flex items-center justify-center shadow-xs hover:bg-secondary/80 transition-all dark:bg-primary dark:text-secondary dark:hover:bg-primary/80"
                      >
                        Become a Member
                      </Link>
                      <ModeToggle />
                    </div>
                  </>
                )}

                {loggedIn && (
                  <>
                    <div className="relative" onMouseEnter={handleProfileEnter} onMouseLeave={handleProfileLeave}>
                      <Button variant="ghost" size="icon" className="rounded-full" aria-label="Profile">
                        <User className="h-10 w-10 text-secondary dark:text-white" />
                      </Button>
                      {profileMenuOpen && (
                        <div className="absolute right-0 top-full mt-2 w-48 rounded-md border bg-popover p-2 shadow-lg z-50">
                          <Link to="/myaccount" className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted">My Account</Link>
                          <Link to="/login" className="block rounded px-3 py-2 text-sm text-popover-foreground hover:bg-muted" onClick={() => { setMobileMenuOpen(false); handleLogout(); }}>Log out</Link>
                        </div>
                      )}
                    </div>
                    <ModeToggle />
                  </>
                )}

              </div>
            </div>
          </div>
        </div>

        {/* Mega Menus */}
        {activeMegaMenu === "categories" && (
          <div className="absolute left-0 top-full w-full border-t bg-popover shadow-lg" onMouseEnter={() => handleMouseEnter("categories")} onMouseLeave={handleMouseLeave}>
            <div className="mx-auto max-w-[1280px] px-4 py-6 sm:px-6 lg:px-8">
              <div className="grid grid-cols-[280px_1fr] gap-8">
                {/* Left Column: Parent Categories */}
                <div className="space-y-1 border-r pr-6">
                  {(categories ?? []).map((cat, index) => (
                    <Link
                      key={cat.id}
                      to={`/catalog/${cat.seName}`}
                      onMouseEnter={() => { setActiveCategory(index); setActiveCategoryId(cat.id); }}
                      onClick={() => { setActiveMegaMenu(null); setActiveCategory(null); setActiveCategoryId(null); }}
                      className={`w-full block rounded px-4 py-2.5 text-left text-sm font-medium transition-all ${
                        activeCategory === index ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>

                {/* Right Column: Subcategories */}
                <div className="pl-2">
                  {activeCategory !== null && (
                    <>
                      <h3 className="mb-4 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        {activeCategory !== null ? (categories?.[activeCategory]?.name ?? "") : ""}
                      </h3>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-3">
                        {activeCategorySubs.map((sub) => (
                          <Link
                            key={sub.id}
                            to={`/catalog/${categories?.[activeCategory ?? 0]?.seName ?? ''}/${sub.seName}`}
                            onClick={() => { setActiveMegaMenu(null); setActiveCategory(null); setActiveCategoryId(null); }}
                            className="rounded px-3 py-2 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-full max-w-sm bg-card shadow-xl">
            <div className="flex h-16 items-center justify-between border-b px-4">
              <Link to={loggedIn ? "/catalog/" : "/"} onClick={() => setMobileMenuOpen(false)}>
                <img src="/bevfed_logo.png" alt="Beverage Federation" width={160} height={48} className="h-[58px] w-auto" />
              </Link>
              <button onClick={() => setMobileMenuOpen(false)} aria-label="Close menu" className="flex items-center justify-center min-h-11 min-w-11">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="overflow-y-auto p-4">
              <div className="space-y-1">
                {/* Marketplace link - only when logged in */}
                {loggedIn && (
                  <Link to="/catalog/" className="flex items-center min-h-[44px] rounded px-3 py-2 font-medium hover:bg-muted flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                    <Store className="h-4 w-4" />
                    Marketplace
                  </Link>
                )}

                {!loggedIn && (
                  <>
                    <Link to="/about" className="flex items-center min-h-[44px] rounded px-3 py-2 font-medium hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                      About
                    </Link>
                    <Link to="/request-demo" className="flex items-center min-h-[44px] rounded px-3 py-2 font-medium hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                      Demo
                    </Link>
                  </>
                )}

                <Link to="/food" className="flex items-center min-h-[44px] rounded px-3 py-2 font-medium hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                  Food Program
                </Link>

                {/* Craft Business Hub - mobile expandable */}
                <div>
                  <div className="flex w-full items-center justify-between rounded px-3 py-2 font-medium hover:bg-muted">
                    <Link to="/craft-business-hub" onClick={() => setMobileMenuOpen(false)}>
                      Craft Business Hub
                    </Link>
                    <button
                      aria-expanded={mobileCraftHubOpen}
                      aria-haspopup="true"
                      onClick={() => setMobileCraftHubOpen((prev) => !prev)}
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileCraftHubOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {mobileCraftHubOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l pl-3">
                      {blogEnabled && (
                        <Link
                          to="/blog"
                          className="flex items-center min-h-[44px] rounded px-3 py-2 text-sm hover:bg-muted"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Blog
                        </Link>
                      )}
                      <Link
                        to="/champions-program"
                        className="flex items-center min-h-[44px] rounded px-3 py-2 text-sm hover:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Champion Program
                      </Link>
                      <Link
                        to="/contract-co-packing-directory"
                        className="flex items-center min-h-[44px] rounded px-3 py-2 text-sm hover:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Contracting &amp; Co-Packing Directory
                      </Link>
                      <Link
                        to="/events"
                        className="flex items-center min-h-[44px] rounded px-3 py-2 text-sm hover:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Events
                      </Link>
                      <a
                        href="https://www.beveragefederation.com/catalog/strategy-and-support"
                        className="flex items-center min-h-[44px] rounded px-3 py-2 text-sm hover:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Services &amp; Strategy
                      </a>
                    </div>
                  )}
                </div>

                {/* Supplier - mobile expandable */}
                <div>
                  <div className="flex w-full items-center justify-between rounded px-3 py-2 font-medium hover:bg-muted">
                    <span>Suppliers &amp; Partners</span>
                    <button
                      aria-expanded={mobileSupplierOpen}
                      aria-haspopup="true"
                      onClick={() => setMobileSupplierOpen((prev) => !prev)}
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileSupplierOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {mobileSupplierOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l pl-3">
                      <a
                        href="https://www.beveragefederation.com/association-program"
                        className="flex items-center min-h-[44px] rounded px-3 py-2 text-sm hover:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Association Program
                      </a>
                      <a
                        href="https://www.beveragefederation.com/become-a-supplier"
                        className="flex items-center min-h-[44px] rounded px-3 py-2 text-sm hover:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Become a Supplier
                      </a>
                      <a
                        href="https://www.beveragefederation.com/portal/login"
                        className="flex items-center min-h-[44px] rounded px-3 py-2 text-sm hover:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Partner Portal
                      </a>
                    </div>
                  )}
                </div>

                <div className="my-4 border-t" />

                {loggedIn ? (
                  <>
                    <Link to="/myaccount" className="flex items-center gap-2 rounded px-3 py-2 hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                      <User className="h-5 w-5 text-secondary dark:text-white" />
                      My Account
                    </Link>
                    <Link to="/login" className="w-full h-11 px-4 py-2 has-[>svg]:px-3 rounded-md bg-primary text-secondary hover:bg-primary/80 font-medium flex items-center justify-center transition-all hover:bg-primary/90" onClick={() => { setMobileMenuOpen(false); handleLogout(); }}>
                      Logout
                    </Link>
                  </>
                ) : (
                  <div className="flex gap-2 flex-col">
                    <Link
                      to="/login"
                      className="h-11 px-4 py-2 has-[>svg]:px-3 rounded-md border border-2 border-secondary dark:border-primary shadow-xs font-medium flex items-center justify-center transition-all hover:bg-secondary/10 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/80"
                    >
                      Login
                    </Link>
                    <Link
                      to="/join"
                      className="h-11 px-4 py-2 has-[>svg]:px-3 rounded-md bg-secondary text-white font-medium flex items-center justify-center shadow-xs hover:bg-secondary/80 transition-all dark:bg-primary dark:text-secondary dark:hover:bg-primary/80"
                    >
                      Become a Member
                    </Link>
                  </div>
                )}

                <div className="my-4 border-t" />
                <div className="flex items-center justify-between rounded px-3 py-2">
                  <span className="font-medium">Dark Mode</span>
                  <ModeToggle />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 z-50 bg-background lg:hidden">
          <div className="flex h-16 items-center gap-2 border-b px-4">
            <button onClick={() => { setMobileSearchOpen(false); setSearchQuery(""); }} aria-label="Close search">
              <X className="h-6 w-6" />
            </button>
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-10"
                autoFocus
              />
            </form>
          </div>
        </div>
      )}
    </>
  );
}